// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import L from 'leaflet';
import Promise from 'bluebird';
import myw from 'myWorld/base/core';
import 'myWorld/base/view';
import config from 'myWorld/base/config';
import './datasource';
import 'myWorld/features/esriFeature';

myw.EsriDatasource = myw.Datasource.extend('EsriDatasource', {
    defaultFeatureModel: myw.EsriFeature,

    statics: {
        layerDefFields: [
            {
                name: 'esriMap',
                type: 'string',
                enumerator: 'datasource.mapNames',
                default: values => (values.length && values[0]) || '',
                viewClass: 'EnumAndTestView',
                args: { testMethod: 'testLayerURL' },
                onChange: 'rebuildForm'
            },
            { name: 'jsClass', type: 'string' },
            {
                name: 'extraOptions',
                type: 'json',
                viewClass: 'KeyValueView',
                args: { keyTitle: 'name', valueTitle: 'value', valType: 'json' }
            }
        ],
        specFields: [
            {
                name: 'url',
                type: 'string',
                size: 'long',
                viewClass: 'UrlInputAndTestView',
                args: { testMethod: 'testURL' }
            },
            { name: 'verifySsl', type: 'boolean', default: true },
            { name: 'esriUseJsonp', type: 'boolean' },
            { name: 'username', type: 'string' },
            { name: 'password', type: 'string' },
            { name: 'authType', type: 'string', enumerator: ['token', 'ntlm'], default: 'token' }
        ],
        geomFieldsFilter(layerDef) {
            return fieldDefs =>
                fieldDefs.filter(
                    fieldDef =>
                        fieldDef.table_name &&
                        fieldDef.table_name.includes(layerDef.spec.esriMap + ':')
                );
        }
    },

    options: {
        useCors: true,
        esriServerType: 'MapServer',
        requestTimeout: 8000,
        url: null,
        verifySsl: true,
        authType: 'token',

        //search related options
        fullQuery: false,
        inWindowQuery: true,
        inSelectionQuery: true
    },

    /**
     * @class Provides access to ESRI services
     * @constructs
     * @augments Datasource
     * @augments IDatasource
     */
    initialize(database, options) {
        if (options.esriUseJsonp) options.useCors = !options.esriUseJsonp;
        myw.Datasource.prototype.initialize.call(this, database, options);

        this.layers = {};
        this.maps = {}; //featureDDs grouped by map name
        this.mapTolayerOptions = {};

        //set the featureDD.name (aka featureType) property
        //and build this.maps
        _.each(this.featuresDD, (featureInfo, key) => {
            const mapName = this._mapNameFor(key);
            featureInfo.name = key;
            if (!this.maps[mapName]) this.maps[mapName] = {};
            this.maps[mapName][key] = featureInfo;
        });

        //initialization
        this.initialized = this._loadLibrary().return(this);
    },

    /**
     * Obtain details about fields necessary to perform a login request
     * @return {promise}
     */
    getAuthOptions() {
        return Promise.resolve({
            auth_fields: [
                {
                    id: 'username',
                    label: 'username',
                    type: 'string'
                },
                {
                    id: 'password',
                    label: 'password',
                    type: 'password'
                }
            ]
        });
    },

    /**
     * Returns the credentials to use for the login request
     * Returns true if it needs credentials by user
     * Returns false if login is not necessary
     * @return {object|boolean}  Keyed on field name
     */
    getLoginCredentials() {
        if (this.options.username)
            return {
                username: this.options.username,
                password: this.options.password
            };
        else return false;
    },

    /**
     * Logs in to the datasource
     * @param  {object} credentials
     * @return {promise}
     */
    login(credentials) {
        if (this.options.authType == 'token') {
            let url = this.options.url.split('/rest/')[0];
            url += '/tokens/generateToken';
            const get = Promise.promisify(L.esri.get);

            return get(url, credentials)
                .then(result => {
                    this.token = result.token;

                    //layers may have been created at this point (when being called by login dialog)
                    //authenticate layers
                    _.each(this.layers, layer => {
                        layer.authenticate(result.token);
                    });
                })
                .catch(errorDetails => {
                    myw.trace('esri', 1, 'Authentication error', errorDetails);
                    throw new myw.AuthenticationError(errorDetails.message);
                });
        } else if (this.options.authType == 'ntlm') {
            // Do nothing (handled automatically by browser)
            return Promise.resolve();
        } else {
            const msg = 'Unknown authorization type: ' + this.options.authType;
            myw.trace('esri', 1, msg);
            throw new myw.AuthenticationError(msg);
        }
    },

    /**
     * Instantiates a leaflet layer from a layer definition
     * @param  {layerDefinition} layerDef
     * @return {ILayer} An esri layer instance
     */
    createLayer(layerDef) {
        const layerDefFixedOptions = myw.Util.processOptionsFromJson(layerDef.extraOptions);
        _.extend(layerDef.options, layerDefFixedOptions);

        const layer = this._createLayer(layerDef);
        layer.on('load', ev => {
            this.system.recordDataAccess(this.database.applicationName, 'layer.' + layerDef.name);
        });

        //store the definition in case it's a dynamically added layer (non stored in configuration)
        this.layerDefs[layerDef.name] = layerDef;
        this.layers[layerDef.name] = layer;
        this.mapTolayerOptions[layerDef.esriMap] = layerDef.options;

        layer.on('authenticationrequired', () => {
            if (this.token) {
                //meanwhile login has succeeded and we have a token
                layer.authenticate(this.token);
            } else {
                layer.fire('invalid');
            }
        });

        return layer;
    },

    _createLayer(layerDef) {
        if (layerDef.category === 'basemap' && !layerDef.jsClass.length) {
            return this._createBaselayer(layerDef);
        }
        const url = this._getUrl(layerDef);

        const options = _.extend(
            {
                url: url,
                token: this.token
            },
            layerDef.options
        );

        const Layer = this._getLayerClassFor(layerDef, L.esri.DynamicMapLayer);
        const _getIdsForLayer = this._getIdsForLayer.bind(this, layerDef);

        const IndependentZoomLayer = Layer.extend({
            onAdd(map) {
                this.setLayersFromZoom(map);
                map.on('moveend', this.setLayersFromZoom.bind(this, map));

                Layer.prototype.onAdd.call(this, map);
            },
            setLayersFromZoom(map) {
                if (!this.setLayers) {
                    console.log('Layer does not implement setLayers()');
                    return;
                }
                const layerIds = _getIdsForLayer(map.getZoom());
                if (layerIds && layerIds.length) this.setLayers(layerIds);
            }
        });

        return new IndependentZoomLayer(options);
    },

    _createBaselayer(layerDef) {
        const map = layerDef.esriMap.replace(/:/g, '/');
        const layer = new L.esri.BasemapLayer({
            urlTemplate: this.options.url + '/' + map + '/MapServer/tile/{z}/{y}/{x}',
            options: layerDef.options
        });
        return layer;
    },

    /**
     * Finds the features selectable by a user map click
     * @param  {L.LatLng}   selectionGeom  Geometry the user created when clicked or when dragged
     * @param  {integer}    zoomLevel       Zoom level at time of selection
     * @param  {integer}    pixelTolerance  Number of pixels to use as tolerance for the selection
     * @param  {array<Layer>}   layers  Layers relevant for selection (active and visible)
     * @return {Promise<Array<Feature>>}  Promise for the features
     */
    select(selectionGeom, zoomLevel, pixelTolerance, layers) {
        const esriMaps = _.unique(layers.map(layer => layer.layerDef.esriMap));
        const map = layers[0].map; //get the map the selection is made on

        const requests = esriMaps.map(esriMapName => {
            const esriLayerIds = this._getIdsForMapName(layers, esriMapName, zoomLevel);

            return this._selectEsriLayerId(
                esriMapName,
                esriLayerIds,
                selectionGeom,
                zoomLevel,
                pixelTolerance,
                map
            ).catch(reason => {
                console.log(
                    `Datasource '${this.options.name}' selection for '${esriMapName}' failed.`,
                    reason
                );
                throw reason;
            });
        });

        return myw.Util.concatPromiseResults(requests);
    },

    /**
     * Handles user selecting via a box
     * @param {L.Object} selectionGeom Leaflet object created by the user on click or drag
     * @param {int} zoomLevel Map zoom level
     * @param {array<Layer>} layers Layers relevant for selection (active and visible)
     */
    selectBox(selectionGeom, zoomLevel, layers) {
        const requests = [];
        //needs a request per esri map and feature type (queries don't take multiple feature types and identify doesn't take a bbox)
        for (let layer of layers) {
            const esriMapName = layer.layerDef.esriMap;
            for (let layerFeatureItem of layer.layerDef.feature_types) {
                requests.push(
                    this._boxSelectEsriLayerId(esriMapName, layerFeatureItem, selectionGeom).catch(
                        reason => {
                            console.log(
                                `Datasource '${this.options.name}': box selection for '${esriMapName}' failed.`,
                                reason
                            );
                            throw reason;
                        }
                    )
                );
            }
        }
        return myw.Util.concatPromiseResults(requests);
    },

    /**
     * Sends box select query to server
     * @param  {string}          esriMapName     Identifier for the Esri map/service
     * @param  {object}          layerFeatureItem    Esri LayerIds to select on. Each layerId referes to a featureType
     * @param  {L.LatLng}        selectionGeom   Geometry the user created when clicked or when dragged
     */
    _boxSelectEsriLayerId(esriMapName, layerFeatureItem, selectionGeom) {
        if (this.options.esriServerType !== 'MapServer') Promise.resolve([]);

        //Create query and send to runner
        const bounds = selectionGeom;
        const featureDD = this.featuresDD[layerFeatureItem.name];
        const url = this._getUrl(esriMapName, featureDD);
        let query = L.esri.query({ url, useCors: this.options.useCors });
        if (bounds) query = query.within(bounds);

        return this._runTask(query, esriMapName, layerFeatureItem);
    },

    /**
     * Finds the features selectable by a user map click
     * @param  {string}          esriMapName     Identifier for the Esri map/service
     * @param  {array}           esriLayerIds    Esri LayerIds to select on. Each layerId referes to a featureType
     * @param  {L.LatLng}        selectionPoint  Point the user clicked/selected
     * @param  {integer}         zoomLevel       Zoom level at time of selection
     * @param  {integer}         pixelTolerance  Number of pixels to use as tolerance for the selection,
     * @param  {string}          esriMapName     Identifier for the Esri map/service
     * @param  {MapControl}  map             Map that triggered the selection
     * @return {Promise<Array<Feature>>}     Promise for the features
     * @private
     */
    _selectEsriLayerId(esriMapName, esriLayerIds, selectionPoint, zoomLevel, pixelTolerance, map) {
        if (this.options.esriServerType !== 'MapServer') Promise.resolve([]);

        const url = this._getUrl(esriMapName);
        const latLng = new L.LatLng(selectionPoint.lat, selectionPoint.lng);

        if (!esriLayerIds) esriLayerIds = undefined;
        else if (esriLayerIds.length > 0) esriLayerIds = 'visible:' + esriLayerIds.join(',');
        else return Promise.resolve([]);

        const task = L.esri
            .identifyFeatures({ url: url, useCors: this.options.useCors })
            .on(map)
            .at(latLng)
            .layers(esriLayerIds)
            .tolerance(pixelTolerance);

        return this._runTask(task, esriMapName);
    },

    /**
     * Sends an external search request
     * @param  {string}         searchTerm      Text to search for
     * @param  {searchOptions}  [options]       Options to influence the search
     * @return {Promise<Array<autoCompleteResult>>}  Promise for autocomplete suggestions to present the user
     */
    runSearch(searchTerm, options) {
        const querySuggestions = this._querySuggestions(searchTerm, this.featuresDD);
        let requests;

        if (!_.isEmpty(querySuggestions)) {
            return Promise.resolve(querySuggestions);
        } else {
            //no queries matched, search for individual features
            const searchDetails = this._getSearchDetailsFor(searchTerm, this.featuresDD);
            requests = _.compact(
                _.map(searchDetails, (searchDetail, featureTypeName) => {
                    if (searchDetail.extraTerms) {
                        return this._searchFeatureTypeForTerm(
                            featureTypeName,
                            searchDetail.extraTerms
                        );
                    }
                })
            );

            return myw.Util.concatPromiseResults(requests);
        }
    },

    /**
     * Sends an external search request
     * @param  {string} featureTypeName
     * @param  {string} terms  Text to search for
     * @return {Promise<Array<autoCompleteResult>>}  Promise for autocomplete suggestions to present the user
     * @private
     */
    _searchFeatureTypeForTerm(featureTypeName, terms) {
        const featureDD = this.featuresDD[featureTypeName];
        const esriMapName = this._mapNameFor(featureTypeName);
        const searchFieldNames = featureDD.search_fields.join(',');
        let layerIds, task;

        if (!searchFieldNames || !searchFieldNames.length) return;

        layerIds = [featureDD.layerId];

        task = L.esri
            .find({ url: this._getUrl(esriMapName), useCors: this.options.useCors })
            .layers(layerIds)
            .text(terms)
            .fields(searchFieldNames);

        return this._runTask(task, esriMapName).then(this._handleSearchResult);
    },

    /**
     * Handles external search results by returning feature Autocomplete suggestions
     * @param  {Array<Feature>} features
     * @return {Promise<Array<autoCompleteResult>>}  Promise for autocomplete suggestions to present the user
     * @private
     */
    _handleSearchResult(features) {
        return _.map(features, feature => {
            const title = feature.getTitle();

            return {
                label: title,
                value: title,
                type: 'feature',
                data: {
                    urn: feature.getUrn()
                }
            };
        });
    },

    /**
     * Sends a query request
     * @param  {object}             queryDef   As returned by runSearch()
     * @param  {queryOptions}     [options]
     * @return {Promise<array<EsriFeature>>} Promise for a list of features
     */
    //ENH: make a call to getFeatures() which will take care of creating queries
    runQuery(queryDef, options) {
        const featureTypeName = queryDef.feature_type,
            esriMapName = this._mapNameFor(featureTypeName),
            featuresDD = this.maps[esriMapName],
            featureDD = featuresDD[featureTypeName],
            url = this._getUrl(esriMapName, featureDD);

        const limit = config['core.queryResultLimit'];
        const query = L.esri.query({ url: url, useCors: this.options.useCors }).limit(limit);

        if (queryDef.clauses) {
            const filter = this._getSqlClauseFor(queryDef.clauses);
            query.where(filter);
        }

        if (options.bounds) query.intersects(options.bounds);

        if (options.polygon) query.intersects(options.polygon);

        return this._runTask(query, esriMapName, featureDD);
    },

    /**
     * Obtains a feature
     * @param  {string}     featureType
     * @param  {integer}    featureId
     * @return {EsriFeature}
     */
    getFeature(featureType, featureId) {
        const esriMapName = this._mapNameFor(featureType);
        const featureTypeInfo = this.featuresDD[featureType];
        const url = this._getUrl(esriMapName, featureTypeInfo);
        let query;

        query = L.esri.query({ url: url, useCors: this.options.useCors }).featureIds([featureId]);

        return this._runTask(query, esriMapName, featureTypeInfo).then(_.first);
    },

    /**
     * Get features of a given table optionally constrained by bounding box
     * @param  {string}             featureType
     * @param  {queryParameters}    [options]       Filters to apply. 'filter' and 'includeTotal' options are not supported
     * @return {Promise<Array<Feature>>}    Promise to resolve with a list of the matched features
     */
    getFeatures(featureType, options) {
        options = options || {};
        const featuresPerReq = 1000;
        const limit = options.limit;
        const offset = options.offset || 0;
        const orderBy = options.orderBy;
        const esriMapName = this._mapNameFor(featureType);
        const featureTypeInfo = this.options.featureTypes[featureType];
        let query = this._createQuery(featureType, options);

        if (orderBy) {
            _.each(orderBy, sort => {
                query = query.orderBy(sort.fieldName, sort.order);
            });
        }

        return this._getAll(query, esriMapName, featureTypeInfo, offset, limit, featuresPerReq);
    },

    /**
     * Count features of a given table optionally constrained by bounding box
     * @param  {string}             featureType
     * @param  {queryParameters}    [options]       Filters to apply to query. 'filter' and 'includeTotal' options are not supported
     * @return {Promise<integer>}    Promise to resolve with a number of the matched features
     */
    countFeatures(featureType, options) {
        const query = this._createQuery(featureType, options || {});
        return this._countTask(query);
    },

    /**
     * Create an Esri query object with given parameters
     * @param  {string}             featureType
     * @param  {queryParameters}    options       Filters to apply on results
     * @return {L.esri.Query}
     * @private
     */
    _createQuery(featureType, params) {
        const bounds = params.bounds;
        const clauses = params.clauses;
        const geom = params.geom;
        const featureTypeInfo = this.options.featureTypes[featureType];
        const esriMapName = this._mapNameFor(featureType);
        const url = this._getUrl(esriMapName, featureTypeInfo);
        let query;
        query = L.esri.query({ url: url, useCors: this.options.useCors });

        if (bounds) query = query.intersects(bounds);

        if (clauses) query = query.where(this._getSqlClauseFor(clauses));

        if (geom) {
            if (_.contains(['MultiPoint', 'MultiPolygon', 'MultiLineString'], geom.type))
                throw new Error('multi-geometry is not supported in ESRI');
            const geojsonFeature = { type: geom.type, coordinates: [geom.coordinates] };
            query = query.intersects(geojsonFeature);
        }

        return query;
    },

    /**
     * Splits a request into chunks of featuresPerReq length
     * This is necessary because some Esri servers only return a maximum of 1000 features per request
     * @param  {L.esri.Query}   query               Query to execute
     * @param  {string}         esriMapName         Name of esri map
     * @param  {object}         featureTypeInfo     Data dictionary for features in results
     * @param  {Integer}        offsetValue         Offset of the results
     * @param  {Integer}        limit               Limit the number of results returned by query
     * @param  {Integer}        featuresPerReq      Number of features to return per request
     * @return {Promise<array<EsriFeature>>}
     * @private
     */
    _getAll(query, esriMapName, featureTypeInfo, offsetValue, limit, featuresPerReq) {
        const requestLimit = Math.min(limit, featuresPerReq) || featuresPerReq;
        query = query.offset(offsetValue);
        query = query.limit(requestLimit);

        return this._runTask(query, esriMapName, featureTypeInfo).then(data => {
            if (data.length < requestLimit || data.length === limit) {
                return data;
            }
            //there are still more features to get
            if (limit) {
                //update limit by removing the number we already got
                limit = limit - requestLimit;
            }

            // update the limit value for each request
            return this._getAll(
                query,
                esriMapName,
                featureTypeInfo,
                offsetValue + featuresPerReq,
                limit,
                featuresPerReq
            ).then(additionalData => data.concat(additionalData));
        });
    },

    /**
     * Obtain the features within distance of a point
     * @param  {string}     featureType
     * @param  {L.LatLng}   point
     * @param  {integer}    radius
     * @return {EsriFeature}
     */
    getNearby(featureType, latlng, radius) {
        //ENH: rename to match getFeaturesAround.
        const esriMapName = this._mapNameFor(featureType);

        const featureTypeInfo = this.options.featureTypes[featureType];
        const url = this._getUrl(esriMapName, featureTypeInfo);
        let query;

        query = L.esri.query({ url: url, useCors: this.options.useCors }).nearby(latlng, radius);

        return this._runTask(query, esriMapName, featureTypeInfo);
    },

    /**
     * Obtains legend information for a given Esri map
     * @param  {layerDefinition} layerDefOrEsriMapName
     * @return {object}
     */
    getLegendInfo(layerDefOrEsriMapName) {
        const token = this.token;
        const url = this._getUrl(layerDefOrEsriMapName) + '/legend';
        const task = L.esri.task({ url: url });
        return new Promise((resolve, reject) => {
            if (token) task.token(token);

            task.request((error, result) => {
                if (error) reject(error);
                else resolve(result);
            });
        });
    },

    /**
     * Loads the Esri leaflet plugin
     * @return {promise}
     * @private
     */
    _loadLibrary() {
        return import(/* webpackChunkName: "esri-leaflet" */ 'esri-leaflet').then(esriLeaflet => {
            L.esri = esriLeaflet;
        });
    },

    /**
     * Returns the map name for a given feature type
     * @param  {string} featureType
     * @return {string}
     * @private
     */
    _mapNameFor(featureType) {
        //the map name is the featureType excluding the last component
        return _.initial(featureType.split(':')).join(':');
    },

    /**
     * Returns the service url for a given layer and (optionally) a feature type
     * @param  {layerDefinition|string} layerDefOrEsriMapName
     * @param  {featureInfo}       [featureInfo]
     * @return {string}
     * @private
     */
    _getUrl(layerDefOrEsriMapName, featureInfo) {
        let url = this.options.url;
        if (url.slice(-1) === '/') url = url.slice(0, -1); //Remove trailing '/'
        let urlParts = url.split('/');
        let esriMapName;

        if (layerDefOrEsriMapName) {
            if (typeof layerDefOrEsriMapName == 'string') {
                esriMapName = layerDefOrEsriMapName;
            } else {
                //layerDef
                esriMapName = layerDefOrEsriMapName.esriMap;
            }

            //Add path to map (handling folder-specific base_url)
            const mapNameParts = esriMapName.split(':');
            const lastUrlPart = urlParts[urlParts.length - 1];
            if (lastUrlPart == mapNameParts[0]) {
                mapNameParts.splice(0, 1);
            }
            urlParts = urlParts.concat(mapNameParts);

            urlParts.push(this.options.esriServerType);

            if (featureInfo) urlParts.push(featureInfo.layerId);
        }

        return urlParts.join('/');
    },

    /**
     * Executes an Esri task processing the results into features
     * Promisification of task.run()
     * @param  {L.esri.Task}    task
     * @param  {string}         esriMapName    Identifier for the Esri map/service
     * @param  {featureInfo}    [featureInfo]    Data dictionary for features in results
     * @return {promise<array<EsriFeature>>}      A bound promise to the results
     * @private
     */
    _runTask(task, esriMapName, featureInfo) {
        const token = this.token;
        return new Promise((resolve, reject) => {
            if (token) task.token(token);
            const layerOptions = this.mapTolayerOptions[esriMapName];

            const callback = (error, featureCollection, response) => {
                if (error) {
                    reject(new Error(error.code + ': ' + error.message));
                } else {
                    resolve([featureCollection, response]);
                }
            };
            const context = undefined;

            // task.run(callback, context) //calling task.run won't pass a context to the request which is necessary for it to handle 'withCredentials' option
            //so we need to call request directly and pass the task as the second argument
            task.request(function (error, response) {
                // immediately invoke with an error
                  if (error) {
                      callback.call(context, error, undefined, response);
                      return;
              
                  // ok no error lets just assume we have features...
                  } else {
                      var featureCollection = L.esri.Util.responseToFeatureCollection(response);
                      response.results = response.results && response.results.reverse();
                      for (var i = 0; i < featureCollection.features.length; i++) {
                        var feature = featureCollection.features[i];
                        if (response.results) feature.layerId = response.results[i].layerId;
                      }
                      callback.call(context, undefined, featureCollection, response);
                  }
              }, { options: layerOptions });
        })
            .timeout(this.options.requestTimeout)
            .then(([featureCollection, response]) =>
                this._processResults(esriMapName, featureInfo, featureCollection, response)
            );
    },

    /**
     * Executes the count operation on a given task
     * @param  {L.esri.Task}    task
     * @return {promise<integer>}        number of features
     * @private
     */
    _countTask(task) {
        const token = this.token;
        return new Promise((resolve, reject) => {
            if (token) task.token(token);
            task.count((error, response) => {
                if (error) {
                    reject(error);
                } else {
                    resolve([response]);
                }
            });
        }).timeout(this.options.requestTimeout);
    },

    /**
     * Processes an Esri task result into instances of myw.EsriFeature
     * @param  {string}     esriMapName     Identifier for the Esri map/service
     * @param  {featureInfo}  featureInfo   Feature type details
     * @param  {object}     featureCollection
     * @param  {object}      response
     * @return {array<EsriFeature>}
     * @private
     */
    _processResults(esriMapName, featureInfo, featureCollection, response) {
        const features = [];
        let types;

        const indexedFeatures = _.indexBy(featureCollection.features, 'id');
        let collection = null;

        if (!featureCollection) return [];

        if (!featureInfo) {
            //we don't know which feature type the results are (select or search requests)
            //the response includes this information per feature
            //get list of different feature types
            types = response.results.map(this._getFeatureTypeFromResult.bind(this, esriMapName));
            types = _.uniq(types);
        } else {
            types = [featureInfo.name];
        }

        return this._ensureDDInfoFor(types)
            .catch(myw.MissingFeatureDD, reason => {
                //can happen for private layers and out of sync DD
                //continue and use data to deduce fields information
            })
            .then(() => {
                if (response.results) {
                    collection = featureCollection;
                } else {
                    //response has the results in correct order
                    //further down we'll get the actual feature data from indexedFeatures
                    collection = response;
                }

                _.each(collection.features, (featureData, index) => {
                    //ENH: refactor out
                    const result = response.results && response.results[index];
                    let featureType =
                        (featureInfo && featureInfo.name) ||
                        this._getFeatureTypeFromResult(esriMapName, result);
                    const featureDD = this.featuresDD[featureType];

                    if (!response.results) {
                        //featureData isn't in the right format, so we need to find the corresponding element
                        //in featureCollection/indexedFeatures
                        const keyName = featureDD.key_name;
                        const key = featureData.attributes[keyName];
                        featureData = indexedFeatures[key];
                    }
                    const props = featureData.properties;

                    if (!featureDD && response.results) {
                        //the esriMap definition doesn't include details for this feature type.
                        //Use information in the response
                        featureType = esriMapName + ':' + result.layerName;
                        featureData.displayFieldName =
                            (result && result.displayFieldName) || response.displayFieldName;
                    }

                    //some requests return the aliased/external field names as the attribute name
                    if (featureDD && featureDD.aliases) {
                        //add an entry for the internal name so our dd can find the value
                        _.each(featureDD.aliases, (alias, fieldName) => {
                            if (!_(props).has(fieldName)) props[fieldName] = props[alias];
                        });
                        if (!featureData.id)
                            featureData.id = featureData.properties[featureDD.key_name];
                    }

                    const esriFeature = this._asFeature(featureData, featureType, true, true);
                    esriFeature.esriMapName = esriMapName;
                    features.push(esriFeature);
                });

                if (response.exceededTransferLimit) features.totalCount = 'Infinity'; //totalcount is used by the resultListControl
                return features;
            });
    },

    /**
     * Obtains the feature type of an item in a service response
     * @param  {string} esriMapName
     * @param  {object} result
     * @return {string}
     * @private
     */
    _getFeatureTypeFromResult(esriMapName, result) {
        const mapDef = this.maps[esriMapName];
        let featureType;

        if (result.layerName) {
            featureType = esriMapName + ':' + result.layerName;
        } else {
            //some services don't include layerName but include layerId
            featureType = _.findKey(
                mapDef,
                e => e.name.indexOf(esriMapName) === 0 && e.layerId == result.layerId
            );
        }
        return featureType;
    },

    /**
     * Obtains the esri layer ids for the feature types associated with the given esri map name
     * @param  {layerDef[]} layeres
     * @param  {string} esriMapName
     * @param  {integer} [zoomLevel] Restrict results to features selectable for [zoomLevel]
     * @return {array<integer>}
     * @private
     */
    _getIdsForMapName(layers, esriMapName, zoomLevel) {
        const layersPerEsriMap = _.groupBy(layers, layer => layer.layerDef.esriMap);
        const mapLayers = layersPerEsriMap[esriMapName];
        //Create a list of unique esrilayerIds because we want only one request per featureType
        //esriLayerId refers to a featureType
        let esriLayerIds = _.chain(mapLayers)
            .map(layer => this._getIdsForLayer(layer.layerDef, zoomLevel))
            .compact()
            .flatten()
            .unique()
            .value();

        if (esriLayerIds.length === 0) esriLayerIds = undefined;

        return esriLayerIds;
    },

    /**
     * Obtains the esri layer ids for the feature types associated with the given layer
     * @param  {layerDef} layerDef
     * @param  {integer} [zoomLevel] Restrict results to features selectable for [zoomLevel]
     * @return {array<integer>}
     * @private
     */
    _getIdsForLayer(layerDef, zoomLevel) {
        const featureTypes = layerDef.feature_types;
        if (!featureTypes) return;

        return featureTypes
            .filter(lfi => {
                if (!zoomLevel) return true;
                return zoomLevel >= lfi.min_select && zoomLevel <= lfi.max_select;
            })
            .filter(featureItem => {
                if (!zoomLevel) return true;
                if (!featureItem.min_vis && !featureItem.max_vis) return true;

                const withinMinRange = zoomLevel >= featureItem.min_vis;
                const withinMaxRange = zoomLevel <= featureItem.max_vis;

                if (featureItem.min_vis && featureItem.max_vis)
                    return withinMinRange && withinMaxRange;
                if (featureItem.min_vis) return withinMinRange;
                if (featureItem.max_vis) return withinMaxRange;

                return false;
            })
            .map(lfi => this.featuresDD[lfi.name].layerId);
    },

    /**
     * Obtains the sql for a given list of clauses
     * @param  {queryClause[]}  clauses
     * @return {string}
     * @private
     */
    _getSqlClauseFor(clauses) {
        let filter = '';
        _.each(clauses, (clause, i) => {
            if (i > 0) {
                filter += ' AND ';
            }

            filter += clause.fieldName + clause.operator;
            filter += typeof clause.value === 'number' ? clause.value : "'" + clause.value + "'";
        });
        return filter;
    },

    /**
     * Tests the URL for the datasource
     * @param  {string} url URL to test
     * @return {Promise}    When resolved returns the metadata returned by the service.
     * @private
     */
    test(url) {
        return new Promise((resolve, reject) => {
            this.initialized.then(() => {
                const service = new L.esri.Service({
                    url: url,
                    useCors: this.options.useCors
                });
                service.metadata((error, metadata) => {
                    if (error) {
                        if (url.length === 0) reject('Error: Please enter a URL');
                        if (error.code === 499) resolve(error.message);
                        else reject(new Error(error.message));
                    } else resolve(metadata);
                });
            });
        });
    },

    /**
     * Returns the url to test the layer def
     * @param  {layerDefinition} layerDef
     * @return {string}
     * @private
     */
    getLayerURL(layerDef) {
        layerDef.esriMap = layerDef.spec.esriMap;
        // set the feature_types just to be able to run the test
        if (_.isEmpty(layerDef.feature_types)) {
            layerDef.feature_types = [{ name: _.keys(this.featuresDD)[0] }];
        }
        return decodeURI(this.createLayer(layerDef, true).options.url);
    },

    /**
     * Tests the datasource url for the layers config page
     * @param  {string} url        URL to test
     * @return {Promise}           When resolved returns an object with a success boolean and a message to alert the user of the success or failure of the test
     * @private
     */
    testLayerURL(url) {
        return this.test(url);
    },

    testURL(showUI = true) {
        const url = this.options.url;
        return this.test(url);
    },

    mapNames() {
        const featureTypes = this.options.feature_types;
        return featureTypes ? _.uniq(featureTypes.map(this._mapNameFor)).sort() : [];
    },

    basemapNames() {
        return Object.keys(L.esri.BasemapLayer.TILES);
    },

    /**
     * Returns the features in a relationship with a given feature. For this datasource only calculated
     * references are support.
     * @param  {Feature}    feature             Feature for which we want the related records
     * @param  {string}         relationshipName    Name of relationship (field)
     * @return {Promise<Feature[]>}    Promise for a list with the features in the relationship
     */
    getRelationship(feature, relationshipName) {
        const fieldDD = feature.featureDD.fields[relationshipName];

        if (!fieldDD) {
            throw new Error(
                `No relationship '${relationshipName}' for feature type: ${feature.getType()}`
            );
        } else if (fieldDD.value && fieldDD.value.indexOf('method(') === 0) {
            const methodName = fieldDD.value.slice(7).split(')')[0];

            if (typeof feature[methodName] !== 'function') {
                throw new Error(
                    `Expected feature '${feature.getUrn()}' to have method named '${methodName}'`
                );
            } else {
                return feature[methodName]().then(result => {
                    if (result instanceof Array) return result;
                    else return [result];
                });
            }
        } else {
            throw new Error(
                `Not a compatible relationship '${relationshipName}' for feature type: ${feature.getType()}`
            );
        }
    }
});

myw.datasourceTypes['esri'] = myw.EsriDatasource;

export default myw.EsriDatasource;
